import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  IconButton,
  ListItemButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

import Api from "../../../API/API";
import cookie from "../../../API/cookie";

import { summarizeRecipeJson, describeRecipeBlocks, describeRecipeBlocksHuman } from "./stimRecipes";

export default function StimulusRecipeManagePage({ onBack }) {

  const [blockLines, setBlockLines] = useState([]);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [selectedSummary, setSelectedSummary] = useState(null);

  const userId = cookie.getCookie("userAccount") ? cookie.getCookie("userAccount") : "";
  const apiToken = cookie.getCookie("accessToken");
  const _rawIsStaff = String(cookie.getCookie("is_staff") ?? "0").toLowerCase();
  const currentUserRole = _rawIsStaff === "true" || _rawIsStaff === "1" ? 1
    : _rawIsStaff === "2" ? 2 : 0;

  const defaultValue = useMemo(() => {
    if (!userId || !apiToken) return null;
    return { key: apiToken };
  }, [userId, apiToken]);

  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [target, setTarget] = useState(null); // {id, name...}

  const [editingAccessRole, setEditingAccessRole] = useState(2);


  const onClickRecipeRow = (r) => {
    let recipeJson = r.recipe;
    if (typeof recipeJson === "string") {
      try { recipeJson = JSON.parse(recipeJson); } catch (e) {}
    }

    const sum = summarizeRecipeJson(recipeJson, r.total_duration_sec);
    const blocks = describeRecipeBlocks(recipeJson);
    
    setSelectedRecipe(r);
    setSelectedSummary(sum);
    setEditingAccessRole(r.access_role ?? 2);
    const blocksHuman = describeRecipeBlocksHuman(recipeJson);
    setBlockLines(blocksHuman);
    setSelectedBlocks(blocks);
    setOpenDetail(true);
  };


  const fetchRecipes = async () => {
    if (!defaultValue) return;
    setLoading(true);
    setError("");
    try {
      // API.js에 getRecipes / getRecipeList 둘 다 있는데, 여기서는 getRecipes 사용
      const res = await Api.getRecipes(defaultValue); // GET /recipes/ :contentReference[oaicite:1]{index=1}
      setRecipes(res?.data ?? []);
    } catch (e) {
      console.error("[RecipeManage] fetch error", e);
      setError("자극 레시피를 불러오지 못했습니다.");
      setRecipes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const openDelete = (recipe) => {
    setTarget(recipe);
    setConfirmOpen(true);
  };

  const closeDelete = () => {
    setConfirmOpen(false);
    setTarget(null);
  };

  const doUpdateAccessRole = async () => {
    if (!selectedRecipe || !defaultValue) return;
    const res = await Api.updateRecipeAccessRole(selectedRecipe.id, editingAccessRole, defaultValue);
    if (!res) {
      alert("권한 변경에 실패했습니다.");
      return;
    }
    setRecipes((prev) =>
      prev.map((r) => r.id === selectedRecipe.id ? { ...r, access_role: editingAccessRole } : r)
    );
    setSelectedRecipe((prev) => ({ ...prev, access_role: editingAccessRole }));
    setOpenDetail(false);
    await fetchRecipes();
  };

  const doDelete = async () => {
    if (!target || !defaultValue) return;
    try {
      const ok = await Api.deleteRecipe(target.id, defaultValue);
      setRecipes((prev) => prev.filter((r) => r.id !== target.id));
      closeDelete();
      if (ok !== true) {
        alert("삭제 실패 (서버에서 삭제되지 않았습니다).");
        return;
      }

      setRecipes(prev => prev.filter(r => r.id !== target.id));
    } catch (e) {
      console.error("[RecipeManage] delete error", e);
      alert("삭제에 실패했습니다. (권한/연결 상태 확인)");
    }
  };

  return (
    <Paper
      style={{
        minHeight: "100vh",
        width: "100%",
        backgroundColor: "#131313",
      }}
    >
      <Box style={{ color: "#CCCCCC" }}>
        <Box
          style={{
            padding: "0px 10px 30px 10px",
            borderRadius: 5,
            width: "90%",
            marginRight: "5%",
            marginLeft: "5%",
          }}
        >
          {/* 헤더 */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              marginTop: 2,
              marginBottom: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: 28, fontFamily: "GmarketSansMedium" }} color="white">
              자극 레시피 관리
            </Typography>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchRecipes}
                sx={{ borderColor: "#CCCCCC", color: "#CCCCCC" }}
              >
                새로고침
              </Button>
              <Button
                variant="outlined"
                onClick={onBack}
                sx={{ borderColor: "#CCCCCC", color: "#CCCCCC" }}
              >
                뒤로
              </Button>
            </Box>
          </Box>

          {/* 상태 */}
          {loading && (
            <Typography sx={{ fontSize: 16, fontFamily: "GmarketSansMedium" }} color="gray">
              불러오는 중…
            </Typography>
          )}
          {!!error && (
            <Typography sx={{ fontSize: 16, fontFamily: "GmarketSansMedium" }} color="red">
              {error}
            </Typography>
          )}

          {/* 리스트 */}
          {!loading && !error && recipes.length === 0 && (
            <Typography sx={{ fontSize: 16, fontFamily: "GmarketSansMedium" }} color="gray">
              저장된 자극 레시피가 없습니다.
            </Typography>
          )}


          <List>
            {recipes.map((r) => (
              <React.Fragment key={r.id}>
                <ListItem
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDelete(r);
                      }}
                    >
                      <DeleteIcon sx={{ color: "#ff6b6b" }} />
                    </IconButton>
                  }
                >
                  <ListItemButton onClick={() => onClickRecipeRow(r)}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography sx={{ fontFamily: "GmarketSansMedium" }} color="white">
                            {r.name || "이름 없는 자극"}
                          </Typography>
                          <Chip
                            label={r.access_role === 1 ? "슈퍼관리자 전용" : "전체 관리자"}
                            color={r.access_role === 1 ? "error" : "default"}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={r.description || ""}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Box>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={confirmOpen} onClose={closeDelete}>
        <DialogTitle>레시피 삭제</DialogTitle>
        <DialogContent>
          <Typography>
            정말 삭제할까요?
            <br />
            <b>{target ? `#${target.id} ${target.name}` : ""}</b>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete}>취소</Button>
          <Button onClick={doDelete} color="error" autoFocus>
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} fullWidth maxWidth="sm">
        <DialogTitle>{selectedRecipe?.name || "이름 없는 자극"}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>{selectedSummary?.summaryLine}</Typography>
          {blockLines?.map((b) => (
            <Typography key={b.index} sx={{ fontSize: 14, color: "#CCCCCC", mt: 1 }}>
              • {b.line}
            </Typography>
          ))}
          {selectedRecipe?.description && (
            <Typography sx={{ mt: 2 }}>{selectedRecipe.description}</Typography>
          )}

          {/* 접근 권한 변경 */}
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ mb: 1, fontSize: 14, color: "#AAAAAA" }}>접근 권한</Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>접근 권한</InputLabel>
              <Select
                label="접근 권한"
                value={editingAccessRole}
                onChange={(e) => setEditingAccessRole(e.target.value)}
              >
                {currentUserRole === 1 && (
                  <MenuItem value={1}>슈퍼관리자 전용</MenuItem>
                )}
                <MenuItem value={2}>전체 관리자 공개</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetail(false)}>닫기</Button>
          <Button variant="contained" onClick={doUpdateAccessRole}>권한 저장</Button>
        </DialogActions>
      </Dialog>
      

      {/* 블록별 상세 */}
      {selectedBlocks?.map((b) => (
        <Box
          key={b.index}
          sx={{
            mt: 2,
            p: 1.5,
            border: "1px solid #333",
            borderRadius: 1,
            backgroundColor: "#1b1b1b",
          }}
        >
          <Typography sx={{ fontFamily: "GmarketSansMedium" }} color="white">
            {b.title}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#AAAAAA", mb: 1 }}>
            {b.metaLine}
          </Typography>

          {b.patternLines?.map((line, idx) => (
            <Typography key={idx} sx={{ fontSize: 13, color: "#CCCCCC" }}>
              - {line}
            </Typography>
          ))}
        </Box>
      ))}
    </Paper>
  );
}
